"use client";

import { Box, Grid, Text } from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { ScrollReveal } from "./ScrollReveal";
import { SectionLabel } from "./SectionLabel";

export function LandingAbout() {
  const tAbout = useTranslations("landing.wrappedInformation");
  const tRedesign = useTranslations("landing.redesign.about_section");
  const [activeIndex, setActiveIndex] = useState(0);

  const cards = [
    {
      tag: tAbout("story.tag"),
      title: tAbout("story.title"),
      body: tAbout("story.description"),
    },
    {
      tag: tAbout("vision.tag"),
      title: tAbout("vision.title"),
      body: tAbout("vision.description"),
    },
    {
      tag: tAbout("roadmap.tag"),
      title: tAbout("roadmap.title"),
      body: tAbout("roadmap.description"),
    },
  ];

  return (
    <Box
      as="section"
      id="about"
      aria-labelledby="landing-about-heading"
      py={{ base: 16, md: 36 }}
      px={{ base: 4, md: 0 }}
      bg="neutral.50"
    >
      <Box maxW="1100px" mx="auto">
        <ScrollReveal>
          <SectionLabel>{tAbout("title")}</SectionLabel>
          <Text
            as="h2"
            id="landing-about-heading"
            fontFamily="heading"
            fontSize={{ base: "2xl", md: "4xl" }}
            fontWeight="semibold"
            letterSpacing="-1px"
            color="neutral.900"
            lineHeight={1.1}
            mb={{ base: 8, md: 12 }}
          >
            {tRedesign("title_line1")}
            <br />
            {tRedesign("title_line2")}
          </Text>
        </ScrollReveal>

        <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
          {cards.map((card, i) => (
            <ScrollReveal key={card.tag} delay={i * 0.1}>
              <Box
                onClick={() => setActiveIndex(i)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setActiveIndex(i);
                  }
                }}
                gridColumn={{
                  base: "span 1",
                  md: activeIndex === i ? "span 6" : "span 3",
                }}
                p={{ base: 5, md: 6 }}
                rounded="2xl"
                border="1px solid"
                borderColor={activeIndex === i ? "primary.500" : "neutral.200"}
                bg={activeIndex === i ? "primary.500" : "white"}
                h="full"
                cursor="pointer"
                transition="all 0.3s ease"
                transform={activeIndex === i ? "scale(1.05)" : "scale(0.98)"}
                boxShadow={
                  activeIndex === i
                    ? "0 20px 48px rgba(73, 77, 238, 0.24)"
                    : "0 8px 24px rgba(0,0,0,0.06)"
                }
                _hover={{
                  bg: activeIndex === i ? "primary.600" : "primary.50",
                  borderColor:
                    activeIndex === i ? "primary.600" : "primary.400",
                  transform:
                    activeIndex === i ? "scale(1.05)" : "translateY(-4px)",
                }}
                _focusVisible={{
                  outline: "2px solid",
                  outlineColor: "primary.400",
                  outlineOffset: "2px",
                }}
              >
                <Text
                  fontFamily="heading"
                  textStyle="xs"
                  fontWeight="bold"
                  letterSpacing="0.1em"
                  color={activeIndex === i ? "whiteAlpha.900" : "primary.500"}
                  textTransform="uppercase"
                  mb={3}
                >
                  {card.tag}
                </Text>
                <Text
                  fontFamily="heading"
                  textStyle="lg"
                  fontWeight="bold"
                  color={activeIndex === i ? "white" : "neutral.900"}
                  mb={3}
                  letterSpacing="-0.3px"
                >
                  {card.title}
                </Text>
                <Text
                  textStyle="xs"
                  color={activeIndex === i ? "whiteAlpha.900" : "neutral.500"}
                  lineHeight={1.7}
                  whiteSpace="pre-line"
                >
                  {card.body}
                </Text>
              </Box>
            </ScrollReveal>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
